import {
  forwardRef,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useImperativeHandle,
} from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import I18n from 'i18n-js';

import ButtonClean from '~/components/ButtonClean';
import {
  ANIMATED_ROOT_POPUP_ID,
  TUseAnimateBottomSheetDialogProps,
  useAnimateBottomSheetDialog,
} from '~/components/molecules/BottomSheetDialog/utils';

import { emitSound, ESCAPE, POP_WINDOW } from '~/redux/actions/SoundActions';
import { useAppDispatch } from '~/redux/store/useAppDispatch';

import { ReactComponent as XIcon } from '~/assets/images/iconsLibrary/x.svg';

import styles from './BottomSheetDialog.module.scss';

export type TBottomSheetDialogElement = {
  close: () => Promise<void>;
};

type TFooterRenderFunctionProps = {
  closeDialog: () => Promise<void>;
};

export type TBottomSheetDialogProps = TUseAnimateBottomSheetDialogProps & {
  header: string;
  className?: string;
  closeButtonClassName?: string;
  contentClassName?: string;
  renderFooter?: ({ closeDialog }: TFooterRenderFunctionProps) => ReactElement;
  shouldEmitSound?: boolean;
  controlled?: boolean;
  dialogLevel?: number;
};

const BottomSheetDialog = forwardRef<
  TBottomSheetDialogElement,
  PropsWithChildren<TBottomSheetDialogProps>
>(
  (
    {
      onClose,
      header,
      children,
      className,
      closeButtonClassName,
      contentClassName,
      beforeClose,
      renderFooter,
      // accepts a string with an id to an element that on desktop will be animated "backwards"
      // when the popup is opened and back to its original state when the popup is closed
      // if `elementIdToAnimate` is not specified, no elements will be animated
      elementIdToAnimate = ANIMATED_ROOT_POPUP_ID,
      shouldEmitSound = false,
      dialogLevel = 0,
    },
    ref,
  ) => {
    const dispatch = useAppDispatch();
    const { dialogElementRef, backgroundElementRef, animateOut } =
      useAnimateBottomSheetDialog({
        onClose,
        beforeClose,
        elementIdToAnimate,
      });

    useImperativeHandle(
      ref,
      () => ({
        close: animateOut,
      }),
      [animateOut],
    );

    useEffect(() => {
      if (shouldEmitSound) {
        dispatch(emitSound(POP_WINDOW));
      }
      return () => {
        if (shouldEmitSound) {
          dispatch(emitSound(ESCAPE));
        }
      };
    }, [dispatch, shouldEmitSound]);

    return ReactDOM.createPortal(
      <div
        className={styles.background}
        style={{ zIndex: 1000 + dialogLevel }}
        ref={backgroundElementRef}
      >
        <div
          className={classNames(styles.dialog, className)}
          ref={dialogElementRef}
        >
          <header className={styles.header}>
            {header}
            <ButtonClean
              aria-label={I18n.t('common_close')}
              className={classNames(styles.closeButton, closeButtonClassName)}
              onClick={animateOut}
            >
              <XIcon />
            </ButtonClean>
          </header>
          <section className={classNames(styles.content, contentClassName)}>
            {children}
          </section>
          {renderFooter ? (
            <footer className={styles.footer}>
              {renderFooter({
                closeDialog: animateOut,
              })}
            </footer>
          ) : null}
        </div>
      </div>,
      document.body,
    );
  },
);

BottomSheetDialog.displayName = 'BottomSheetDialog';

export default BottomSheetDialog;
